/**
 * CommandRegistry - コマンドを一元管理するレジストリ
 *
 * TabRegistryのパターンを踏襲し、コマンドハンドラを登録・取得する
 */

import type { CommandHandler, CommandCategory } from './types';
import logger from '../utils/logger';

class CommandRegistryImpl {
  private registry = new Map<string, CommandHandler>();
  private aliases = new Map<string, string>();  // alias -> canonical name

  /**
   * コマンドハンドラを登録
   */
  register(handler: CommandHandler): void {
    if (this.registry.has(handler.name)) {
      logger.warn(`[CommandRegistry] Command "${handler.name}" is already registered. Overwriting.`);
    }

    this.registry.set(handler.name, handler);

    // エイリアスも登録
    if (handler.aliases) {
      for (const alias of handler.aliases) {
        if (this.aliases.has(alias)) {
          logger.warn(`[CommandRegistry] Alias "${alias}" is already registered.`);
        }
        this.aliases.set(alias, handler.name);
      }
    }

    logger.debug(`[CommandRegistry] Registered command: ${handler.name}`);
  }

  /**
   * コマンドハンドラを取得（名前またはエイリアスで検索）
   */
  get(nameOrAlias: string): CommandHandler | undefined {
    // まずエイリアスを解決
    const canonicalName = this.aliases.get(nameOrAlias) ?? nameOrAlias;
    return this.registry.get(canonicalName);
  }

  /**
   * 全コマンドハンドラを取得
   */
  getAll(): CommandHandler[] {
    return Array.from(this.registry.values());
  }

  /**
   * カテゴリでフィルタしてコマンドハンドラを取得
   */
  getByCategory(category: CommandCategory): CommandHandler[] {
    return Array.from(this.registry.values())
      .filter(handler => handler.category === category);
  }

  /**
   * コマンド名のプレフィックスで補完候補を取得
   */
  getCompletions(prefix: string): CommandHandler[] {
    const results: CommandHandler[] = [];
    const seen = new Set<string>();

    // コマンド名で検索
    for (const [name, handler] of this.registry) {
      if (name.startsWith(prefix) && !seen.has(name)) {
        results.push(handler);
        seen.add(name);
      }
    }

    // エイリアスでも検索
    for (const [alias, canonical] of this.aliases) {
      if (alias.startsWith(prefix) && !seen.has(canonical)) {
        const handler = this.registry.get(canonical);
        if (handler) {
          results.push(handler);
          seen.add(canonical);
        }
      }
    }

    return results;
  }

  /**
   * 登録されているコマンド数を取得
   */
  get size(): number {
    return this.registry.size;
  }

  /**
   * レジストリをクリア（テスト用）
   */
  clear(): void {
    this.registry.clear();
    this.aliases.clear();
  }

  /**
   * コマンドが登録されているか確認
   */
  has(nameOrAlias: string): boolean {
    const canonicalName = this.aliases.get(nameOrAlias) ?? nameOrAlias;
    return this.registry.has(canonicalName);
  }

  /**
   * 登録されているコマンド名の配列を取得
   */
  getNames(): string[] {
    return Array.from(this.registry.keys());
  }
}

// シングルトンインスタンス
export const commandRegistry = new CommandRegistryImpl();
